/**
 * Message bodies as tokens — **the shared kernel** (amendment 25 §9: *build them
 * once, mount them in both places*).
 *
 * This was the recall builder's model, sitting inside `recall-builder/`. The
 * reminders editor needs exactly the same thing over a different set of fields —
 * appointment date, provider, the change-the-time link — so the model moved here
 * and took a type parameter. Nothing in this file knows what a recall is.
 *
 * The rule the token array exists to protect: **a field is a token, never braces
 * in a string.** Braces in a string mean a typo ships as literal `{frist_name}`
 * in a patient's text. Here a typo is an `unknown` token, which renders red and
 * blocks the step.
 */

export type MsgToken<K extends string = string> =
  | { kind: 'text'; text: string }
  | { kind: 'field'; field: K }
  /** Braces somebody typed that match no field. Blocks the step. */
  | { kind: 'unknown'; raw: string };

/** What the menu needs to know about a field. Both editors supply their own. */
export interface MsgField<K extends string = string> {
  key: K;
  label: string;
  /** Shown beside the label, so the writer sees what it becomes. */
  example: string;
  /** Some patients or appointments lack it, so a message using it needs a fallback. */
  mayBeEmpty: boolean;
  defaultFallback?: string;
}

export interface MsgMenuEntry<K extends string = string> {
  field: MsgField<K>;
  /** Set when the field cannot be used here — shown greyed **with** this reason. */
  unavailable: string | undefined;
}

/** Merge neighbouring text runs so the array reads the way the box looks. */
export function mergeText<K extends string>(nodes: MsgToken<K>[]): MsgToken<K>[] {
  const out: MsgToken<K>[] = [];
  for (const n of nodes) {
    const prev = out[out.length - 1];
    if (n.kind === 'text' && prev?.kind === 'text') {
      out[out.length - 1] = { kind: 'text', text: prev.text + n.text };
    } else out.push(n);
  }
  return out;
}

/**
 * Split a run on `{...}`. A complete match becomes a field token; a complete
 * non-match becomes `unknown`, which renders red and blocks the step.
 *
 * `known` is passed in rather than imported, which is the whole point of this
 * file: the reminders editor's field names are not the recall editor's.
 */
export function convertCompleteBraces<K extends string>(
  raw: string,
  known: ReadonlySet<string>,
): MsgToken<K>[] {
  const out: MsgToken<K>[] = [];
  const re = /\{\{?([a-z_]+)\}?\}/gi;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: raw.slice(last, m.index) });
    const key = (m[1] ?? '').toLowerCase();
    if (known.has(key)) out.push({ kind: 'field', field: key as K });
    else out.push({ kind: 'unknown', raw: m[0] });
    last = m.index + m[0].length;
  }
  if (out.length === 0) return [{ kind: 'text', text: raw }];
  if (last < raw.length) out.push({ kind: 'text', text: raw.slice(last) });
  return out;
}

/** The same, applied across a token list's text runs. */
export function convertCompleteBracesIn<K extends string>(
  tokens: MsgToken<K>[],
  known: ReadonlySet<string>,
): MsgToken<K>[] {
  const out: MsgToken<K>[] = [];
  for (const t of tokens) {
    if (t.kind === 'text') out.push(...convertCompleteBraces<K>(t.text, known));
    else out.push(t);
  }
  return mergeText(out);
}

/** The words, with every chip read as its label — for a one-line summary. */
export function tokenPlain<K extends string>(
  tokens: MsgToken<K>[],
  label: (key: K) => string,
): string {
  return tokens
    .map((t) => (t.kind === 'text' ? t.text : t.kind === 'field' ? label(t.field) : t.raw))
    .join('');
}

export interface ResolvedField<K extends string> {
  field: K;
  label: string;
  value: string;
  /** The patient had none on file, so this is the fallback — and it says so. */
  wasFallback: boolean;
}

export interface Resolved<K extends string> {
  text: string;
  used: ResolvedField<K>[];
}

/**
 * Fill the tokens in for one patient. A field the patient lacks takes its
 * fallback **and is reported as a fallback**, so a preview can never look right
 * for the wrong reason.
 */
export function resolveTokens<K extends string>(
  tokens: MsgToken<K>[],
  values: Partial<Record<K, string>>,
  fallbacks: Partial<Record<K, string>> | undefined,
  fields: MsgField<K>[],
): Resolved<K> {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const used: ResolvedField<K>[] = [];
  let text = '';
  for (const t of tokens) {
    if (t.kind === 'text') {
      text += t.text;
      continue;
    }
    if (t.kind === 'unknown') {
      text += t.raw;
      continue;
    }
    const def = byKey.get(t.field);
    const real = values[t.field];
    const fallback = fallbacks?.[t.field] ?? def?.defaultFallback ?? '';
    const value = real !== undefined && real !== '' ? real : fallback;
    used.push({
      field: t.field,
      label: def?.label ?? t.field,
      value,
      wasFallback: real === undefined || real === '',
    });
    text += value;
  }
  return { text, used };
}

/** Fields a body actually uses, in order, without repeats. */
export function fieldsUsedIn<K extends string>(...lists: (MsgToken<K>[] | undefined)[]): K[] {
  const seen: K[] = [];
  for (const list of lists) {
    for (const t of list ?? []) {
      if (t.kind === 'field' && !seen.includes(t.field)) seen.push(t.field);
    }
  }
  return seen;
}

/** An unknown token anywhere means the step cannot be completed. */
export function hasUnknown<K extends string>(...lists: (MsgToken<K>[] | undefined)[]): boolean {
  return lists.some((l) => (l ?? []).some((t) => t.kind === 'unknown'));
}

/** Build a token list from alternating text and field keys. */
export function tk<K extends string>(known: ReadonlySet<string>, ...parts: string[]): MsgToken<K>[] {
  return parts.map((p) =>
    known.has(p) ? ({ kind: 'field', field: p as K } as const) : ({ kind: 'text', text: p } as const),
  );
}
